// Shared email display formatting — dedupes the shortWhen/status/category-color copies
// that used to live in InboxEmailRow.tsx, InboxOutlook.tsx, and AccountDetail.tsx.

export function shortWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  // ENG-97: older mail keeps its time too ("Sep 14, 3:42 PM"); the year appears only
  // for a prior year so cross-year search results stay unambiguous.
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    ...(d.getFullYear() !== now.getFullYear() ? { year: "numeric" as const } : {}),
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Full timestamp for a hover tooltip on the short list-row date. */
export function fullWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
}

/** email_tasks status → label (4-state; "new" reads as claimed-but-unassigned). */
export const EMAIL_TASK_STATUSES = ["new", "assigned", "in_progress", "resolved"] as const;
export const emailTaskStatusLabel: Record<string, string> = {
  new: "New",
  assigned: "Assigned",
  in_progress: "In Progress",
  resolved: "Resolved",
};
export const emailTaskStatusColor: Record<string, string> = {
  new: "bg-slate-100 text-slate-700",
  assigned: "bg-violet-100 text-violet-700",
  in_progress: "bg-blue-100 text-blue-700",
  resolved: "bg-green-100 text-green-700",
};

// ponytail: Outlook-ish category palette — deterministic name→color via a charcode-sum hash
// (reading each user's real Outlook master-list colors would need MailboxSettings.Read).
const CATEGORY_COLORS = [
  "bg-red-100 text-red-700",
  "bg-orange-100 text-orange-700",
  "bg-yellow-100 text-yellow-700",
  "bg-green-100 text-green-700",
  "bg-teal-100 text-teal-700",
  "bg-blue-100 text-blue-700",
  "bg-purple-100 text-purple-700",
  "bg-pink-100 text-pink-700",
  "bg-amber-100 text-amber-800", // brown-ish stand-in (Tailwind has no "brown")
  "bg-neutral-200 text-neutral-700",
];
export function categoryColor(name: string): string {
  let sum = 0;
  for (let i = 0; i < name.length; i++) sum += name.charCodeAt(i);
  return CATEGORY_COLORS[sum % CATEGORY_COLORS.length];
}

/** Initials for a small avatar circle (2 letters). "Cassie Carpenter" -> "CC",
 * single word -> first 2 letters, empty/whitespace-only -> "?". */
export function initialsFor(name: string | null | undefined): string {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return parts[0].slice(0, 2).toUpperCase();
}

// ponytail: same deterministic-hash approach as categoryColor, just a different palette
// tuned for solid avatar-circle backgrounds (white text on top).
const AVATAR_COLORS = [
  "bg-rose-500",
  "bg-orange-500",
  "bg-amber-500",
  "bg-emerald-500",
  "bg-teal-500",
  "bg-blue-500",
  "bg-indigo-500",
  "bg-violet-500",
  "bg-pink-500",
  "bg-slate-500",
];
export function avatarColor(name: string): string {
  let sum = 0;
  for (let i = 0; i < name.length; i++) sum += name.charCodeAt(i);
  return AVATAR_COLORS[sum % AVATAR_COLORS.length];
}

/** Tailwind classes for a compact email-task status chip (4-state vocabulary). */
export function statusChipClass(status: string | null | undefined): string {
  if (!status) return emailTaskStatusColor.new;
  return emailTaskStatusColor[status] ?? emailTaskStatusColor.new;
}

// ENG-98: compact display for the "originally sent to" tag on shared-inbox rows.
// Handles "First Last", Exchange's "Last, First", and quoted display names;
// falls back to the address local-part when the archive has no display name.
export function firstNameOf(name: string | null | undefined, address: string): string {
  let n = (name ?? "").trim();
  // strip one layer of surrounding quotes ("Jennifer Fulton", 'Fulton, Jennifer')
  if (n.length >= 2 && ((n.startsWith('"') && n.endsWith('"')) || (n.startsWith("'") && n.endsWith("'")))) {
    n = n.slice(1, -1).trim();
  }
  if (n.includes(",")) n = n.split(",")[1]?.trim() ?? "";
  const first = n.split(/\s+/)[0] ?? "";
  if (first) return first;
  const addr = (address ?? "").trim();
  if (!addr) return "";
  const at = addr.indexOf("@");
  return at > 0 ? addr.slice(0, at) : addr;
}

interface StaffRecipient {
  name: string;
  address: string;
}

/** v1.5.69: the one badge for both routing shapes on shared-inbox rows.
 *
 * - routed (originallyTo set): "→ Jennifer" / "→ Jennifer +1" — the shared mailbox was in
 *   neither To nor Cc; a transport-rule BCC copy of a staffer's mail (ENG-98).
 * - direct (staff besides the mailbox in To/Cc): "Jennifer" / "Jennifer +1" — the mailbox
 *   WAS addressed but staff hold their own copies too; the arrow's absence is the signal.
 *
 * Both surfaces (EmailRow + the pop-out list rows) render the same label/tooltip, so the
 * math lives here, unit-tested, not in two components. internalRecipients missing on an
 * old cached payload degrades to the pre-v1.5.69 single-name tag. */
export function internalDeliveryBadge(
  originallyTo: StaffRecipient | null | undefined,
  internalRecipients: StaffRecipient[] | null | undefined,
): { label: string; tooltip: string; routed: boolean } | null {
  const staff =
    internalRecipients && internalRecipients.length > 0
      ? internalRecipients
      : originallyTo
        ? [originallyTo]
        : [];
  if (staff.length === 0) return null;

  const extra = staff.length > 1 ? ` +${staff.length - 1}` : "";
  if (originallyTo) {
    const others = staff.filter(
      (r) => r.address.toLowerCase() !== originallyTo.address.toLowerCase(),
    );
    const alsoLine = others.length > 0 ? `. Also sent to ${others.map((r) => r.address).join(", ")}` : "";
    return {
      label: `→ ${firstNameOf(originallyTo.name, originallyTo.address)}${extra}`,
      tooltip: `Originally sent to ${originallyTo.address} — copied to this shared inbox by mail routing${alsoLine}`,
      routed: true,
    };
  }
  return {
    label: `${firstNameOf(staff[0].name, staff[0].address)}${extra}`,
    tooltip: `Also sent directly to ${staff.map((r) => r.address).join(", ")} — they received their own copy of this email`,
    routed: false,
  };
}
